import {useEffect, useReducer} from 'react';
import AddressBook from '@util/AddressBook';
import {getCheckoutPaymentKey} from '@util/CheckoutPaymentStorage';
import {checkoutInitializer, checkoutReducer, useCheckoutActions} from '@reducers/checkout';
import User from '@modules/User';
import {
    shouldRequirePaymentValidation,
    shouldRequirePONumber
} from '@containers/Checkout/salesChannel';

export default function useCheckoutApi ( customerData ) {

    // note ** Required business logic **
    //  ** These steps have to happen in order for checkout fields to be valid **
    //  - 1.1 Default to previous address selection from cart OR default to primary SHIP_TO address;
    //  ! SHIP_TO address should always be available and preselected otherwise Account Detail is in bad state checkout should not be allowed
    //  2. Request "Shipping Options" from API once we know the SHIP_TO address;
    //  2.1 Default to cheapest option (STANDARD)
    //  handle error if no shipping options are returned
    //  3. Payment Options and Billing Address are tied together.
    //  3.1 Default to previous Payment method from cart;
    //  -- If a Payment method is expired it will not be returned from the API
    //  3.2 if Payment "Terms" a BILL_TO address should NOT be selected by default to avoid wrong address due to
    //  customer not double checking selection (this has been a reported issue from CS). Although once a BILL_TO is
    //  selected if customer switches between payment options, the BILL_TO should be retained.
    //  4. PO number should be required for booked LGP/GOV/UNIV orders. It is optional for approval submissions and
    //  PunchOut checkout.

    const {wallet, cart, addresses, account, isPunchOut} = customerData;
    const initialState = {
        addressBook: new AddressBook( {addresses} ),
        paymentOptions: User.Wallet.normalizeWallet( wallet ),
        PONumber: cart.poNumber,
        promotions: cart.promotions
    }
    const [state, dispatch] = useReducer( checkoutReducer, initialState, checkoutInitializer( customerData ) );
    const actions = useCheckoutActions( dispatch );

    /**
     * Request shipping options for the selected address and ignore the result when the
     * address changes or checkout unmounts.
     */
    useEffect( () => {
        let ignore = false;

        if ( !state.selectedShippingAddress ) {
            actions.setCheckoutErrors( {
                shippingAddress: 'No valid shipping address found'
            } );
            return undefined;
        }

        actions.setShippingOptionsForSelectedAddress( {
            shouldIgnore: () => ignore
        } );

        return () => {
            ignore = true;
        };
    }, [state.selectedShippingAddress] );

    useEffect( () => {
        actions.setPaymentOptions( User.Wallet.normalizeWallet( wallet ) );
        actions.setAddressBook( new AddressBook( {addresses} ) );
    }, [wallet, addresses] );

    const paymentSelectionKey = getCheckoutPaymentKey( customerData );
    useEffect( () => {
        if ( paymentSelectionKey !== state.paymentSelectionKey ) {
            actions.reinitializeCheckoutState( customerData );
        }
    }, [paymentSelectionKey, state.paymentSelectionKey] );

    useEffect( () => {
        const isApprovalRequired = cart.approvalRequired === true;
        const isPORequired = shouldRequirePONumber( customerData );
        const isPaymentValidationRequired = shouldRequirePaymentValidation( customerData );

        // A new cart can supersede the locally applied pricing result even when the boolean value
        // matches the customer snapshot that existed before pricing.
        actions.setApprovalRequired( isApprovalRequired, isPORequired, isPaymentValidationRequired );
    }, [cart, account?.salesChannel, customerData.isAdmin, customerData.ordersNeedApproval, isPunchOut] );

    async function onPriceOrderReviewCart () {
        const pricedCart = await actions.priceCart();

        if ( pricedCart ) {
            const pricedCustomerData = {...customerData, cart: pricedCart};
            actions.setApprovalRequired(
                pricedCart.approvalRequired === true,
                shouldRequirePONumber( pricedCustomerData ),
                shouldRequirePaymentValidation( pricedCustomerData )
            );
        }

        return pricedCart;
    }

    async function onProcessOrder () {
        const {isValid, validationErrors} = actions.validateCheckout( state );

        if ( !isValid ) {
            console.error( '[Checkout not valid]', validationErrors );
            return Promise.reject( validationErrors );
        }

        if ( state.isPunchOut ) {
            return actions.processPunchOutOrder( state );
        }

        return actions.processOrder( state );
    }

    return {
        ...state,
        ...actions,
        onRefetchShippingOptions: actions.setShippingOptionsForSelectedAddress,
        onAddPromotion: value => actions.updatePromotion( 'add', value ),
        onRemovePromotion: value => actions.updatePromotion( 'remove', value ),
        onShippingAddressChange: actions.setSelectedShippingAddress,
        onShippingOptionChange: actions.setSelectedShippingOption,
        onPriceOrderReviewCart,
        onPaymentOptionChange: actions.setSelectedPaymentOption,
        onPaymentAdded: ( addedPayment ) =>
            actions.setSelectedPaymentOption(
                User.Wallet.normalizeCreditCard( addedPayment )
            ),
        onBillingAddressChange: actions.setSelectedBillingAddress,
        onBillingAddressAdded: actions.setSelectedBillingAddress,
        onPONumberChange: actions.setPONumber,
        onProcessOrder,
        reinitializeCheckoutState: () => actions.reinitializeCheckoutState( customerData )
    };
}
